import { Badge } from "@tellescope/ui";

import type { TokenDefinition } from "../tokens/types";
import { TOKEN_DND_MIME, TOKEN_ID_DND_MIME } from "./tokenDrag";
import type { CollectorAnchor } from "./useTokenPalette";

export interface TokenBadgeProps {
  definition: TokenDefinition;
  onSelect: (definition: TokenDefinition, anchor: CollectorAnchor) => void;
}

/** Center of an element in viewport coordinates — where a click-opened collector anchors. */
const centerOf = (element: HTMLElement): CollectorAnchor => {
  const rect = element.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
};

/**
 * A palette entry. Click (or Enter/Space) inserts the token, and every badge is draggable into the
 * editor. A param-less badge carries its built raw ({@link TOKEN_DND_MIME}) so the drop inserts a
 * chip immediately; a parameterized badge carries its id ({@link TOKEN_ID_DND_MIME}) instead — it
 * has no raw yet — so the drop opens the collector to gather params before inserting.
 */
export const TokenBadge = ({ definition, onSelect }: TokenBadgeProps) => {
  const isParameterized = Boolean(definition.params?.length);

  return (
    <Badge
      variant="secondary"
      role="button"
      tabIndex={0}
      draggable
      title={isParameterized ? undefined : definition.build()}
      onClick={(event) => onSelect(definition, centerOf(event.currentTarget))}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(definition, centerOf(event.currentTarget));
        }
      }}
      onDragStart={(event) =>
        isParameterized
          ? event.dataTransfer.setData(TOKEN_ID_DND_MIME, definition.id)
          : event.dataTransfer.setData(TOKEN_DND_MIME, definition.build())
      }
      className="cursor-grab font-normal"
    >
      {definition.getLabel()}
    </Badge>
  );
};
